import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PaperPlaneTilt, Robot, Copy, Check } from "@phosphor-icons/react";
import axios from "axios";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

function ChatWidgetPreview({ config, websiteId, authHeaders }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setSending(true);
    try {
      // ✅ FIX: /api/chat (JWT required) → /api/chat/public (no auth needed)
      // Live Preview dashboard mein hoti hai lekin public endpoint use karni chahiye
      // taaki widget ke jaisa exact behavior mile
      const res = await axios.post(
        `${API}/chat/public`,
        { website_id: websiteId, message: text },
        { headers: { "Content-Type": "application/json" } }
      );
      setMessages((prev) => [...prev, { role: "bot", text: res.data.answer }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: err.response?.data?.detail || "Something went wrong. Please try again." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const primaryColor = config?.primary_color || "#002FA7";

  return (
    <div className="border border-[#E5E7EB] flex flex-col h-[480px]" data-testid="chat-widget-preview">
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-3" style={{ backgroundColor: primaryColor }}>
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <Robot size={18} weight="bold" className="text-white" />
        </div>
        <div>
          <div className="text-sm font-semibold text-white">{config?.bot_name || "AI Assistant"}</div>
          <div className="text-[10px] text-white/60">Online</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9FAFB]">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Robot size={32} weight="thin" className="mx-auto text-[#D1D5DB] mb-2" />
            <p className="text-sm text-[#6B7280]">{config?.welcome_message || "Hi! How can I help you today?"}</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "text-white"
                  : "bg-white border border-[#E5E7EB] text-[#0A0A0A]"
              }`}
              style={msg.role === "user" ? { backgroundColor: primaryColor } : {}}
            >
              {msg.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm text-[#6B7280]">
              Thinking...
            </div>
          </div>
        )}
      </div>

      <form onSubmit={sendMessage} className="border-t border-[#E5E7EB] p-3 flex gap-2 bg-white">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message..."
          className="rounded-none border-[#D1D5DB] flex-1 text-sm"
          disabled={sending}
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={sending || !input.trim()}
          className="rounded-none h-9 w-9 p-0 flex items-center justify-center"
          style={{ backgroundColor: primaryColor }}
          data-testid="chat-send-button"
        >
          <PaperPlaneTilt size={16} weight="bold" className="text-white" />
        </Button>
      </form>
    </div>
  );
}

export default function ChatbotConfig() {
  const { websiteId } = useParams();
  const { getAuthHeaders } = useAuth();
  const [website, setWebsite] = useState(null);
  const [chatbotConfig, setChatbotConfig] = useState({
    primary_color: "#002FA7",
    welcome_message: "Hi! How can I help you today?",
    bot_name: "AI Assistant",
    position: "bottom-right",
    theme: "light",
  });
  const [embedCode, setEmbedCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    try {
      const [websiteRes, configRes, widgetRes] = await Promise.all([
        axios.get(`${API}/websites/${websiteId}`, { headers }),
        axios.get(`${API}/chatbot-config/${websiteId}`, { headers }),
        axios.get(`${API}/widget/${websiteId}`, { headers }),
      ]);
      setWebsite(websiteRes.data);
      setChatbotConfig(configRes.data);
      setEmbedCode(widgetRes.data.embed_code);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [websiteId, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/chatbot-config/${websiteId}`,
        chatbotConfig,
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm font-mono uppercase tracking-[0.2em] text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="chatbot-config-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Chatbot Configuration</h1>
        <p className="text-sm text-[#6B7280] mt-1">{website?.name || website?.url}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="border border-[#E5E7EB] p-6 space-y-6">
          <h2 className="font-heading text-lg font-bold">Settings</h2>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">Bot Name</Label>
            <Input
              value={chatbotConfig.bot_name}
              onChange={(e) => setChatbotConfig({ ...chatbotConfig, bot_name: e.target.value })}
              className="rounded-none border-[#D1D5DB]"
              data-testid="config-bot-name"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">Welcome Message</Label>
            <Input
              value={chatbotConfig.welcome_message}
              onChange={(e) => setChatbotConfig({ ...chatbotConfig, welcome_message: e.target.value })}
              className="rounded-none border-[#D1D5DB]"
              data-testid="config-welcome-message"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">Primary Color</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={chatbotConfig.primary_color}
                onChange={(e) => setChatbotConfig({ ...chatbotConfig, primary_color: e.target.value })}
                className="w-10 h-10 border border-[#D1D5DB] cursor-pointer"
                data-testid="config-color-picker"
              />
              <Input
                value={chatbotConfig.primary_color}
                onChange={(e) => setChatbotConfig({ ...chatbotConfig, primary_color: e.target.value })}
                className="rounded-none border-[#D1D5DB] w-32 font-mono text-sm"
                data-testid="config-color-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">Dark Theme</Label>
            <Switch
              checked={chatbotConfig.theme === "dark"}
              onCheckedChange={(checked) => setChatbotConfig({ ...chatbotConfig, theme: checked ? "dark" : "light" })}
              data-testid="config-theme-switch"
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white h-11"
            data-testid="config-save-button"
          >
            {saving ? "Saving..." : "Save Configuration"}
          </Button>

          <div className="pt-4 border-t border-[#E5E7EB] space-y-3">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">Embed Code</Label>
            <div className="relative">
              <pre className="bg-[#0A0A0A] text-white/80 p-4 text-xs font-mono overflow-x-auto" data-testid="embed-code-block">
                {embedCode}
              </pre>
              <button
                onClick={handleCopyEmbed}
                className="absolute top-2 right-2 p-1.5 text-white/40 hover:text-white transition-colors"
                data-testid="copy-embed-button"
              >
                {copied ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
              </button>
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-4">Live Preview</div>
          <ChatWidgetPreview config={chatbotConfig} websiteId={websiteId} authHeaders={getAuthHeaders()} />
        </div>
      </div>
    </div>
  );
}
